/**
 * Starter prompts, shown only on an empty conversation.
 *
 * These do real work beyond saving typing: they teach billers what Remi can do.
 * ChatMS v1 ships three capabilities, so the defaults deliberately show one of each
 * rather than three variations of the same thing:
 *
 *   1. LIVE DATA  - claim and denial questions answered from the billing APIs
 *   2. CODING     - CPT / ICD / modifier explanation and claim coding review
 *   3. HOW-TO     - "where do I do X", answered with a link to the right screen
 *
 * A biller who sees only denial prompts assumes that's all it does.
 *
 * Ordering: prompts using an identifier already on screen come first - they are the
 * highest value because the biller types nothing at all.
 */

const MAX_SUGGESTIONS = 4;

/** Claim is on screen: nothing to type, immediate payoff. */
const claimPrompts = (claimId) => [
  `Why was claim ${claimId} denied and what do I do next?`,
  `Review the coding on claim ${claimId}`,
  `What's happened on claim ${claimId} so far?`,
];

/**
 * Patient on screen but no specific claim.
 *
 * The insurance prompt leads because it's the most common pre-submission check, and
 * it's phrased as "coverage" rather than "active" deliberately - "active" conflates
 * three different things (on file / effective on the DOS / verified with the payer)
 * and the useful answer distinguishes them.
 */
const PATIENT_PROMPTS = [
  "What insurance does this patient have, and is it current?",
  "Was this patient's coverage active on their last date of service?",
  "Does this patient have active secondary insurance?",
  "Show this patient's recent payments and copays",
  "What visits has this patient had this year?",
];

/**
 * Per-screen prompts. Keys match sScreenName with any " V2" suffix stripped,
 * so Claim Status and Claim Status V2 share one entry.
 */
const BY_SCREEN = {
  "Claim Status": [
    "Which claims are expiring in the next 15 days?",
    "Show denied claims from the last 7 days",
    "What's the difference between denied and rejected?",
  ],
  "Denial Management": [
    "What are our top denial reasons this month?",
    "Which denials are still inside the appeal window?",
    "What does CO-197 mean and how do we fix it?",
  ],
  "Workable Claims": [
    "What should I work first today?",
    "Why are these claims in validation-failed?",
    "Which of these are close to timely-filing?",
  ],
  "Posting Payments": [
    "How do I post an ERA that only partially paid?",
    "What's the difference between a write-off and an adjustment?",
    "Where do I apply a copay?",
  ],
  Remittance: [
    "How do I reprocess a remittance file that failed?",
    "What happens when an 835 matches more than one claim?",
  ],
  "AR Aging Report": [
    "Which payer has the largest balance over 120 days?",
    "What's our AR over 90 days by payer?",
  ],
  "AR Aging Claim Details": ["Why is this claim still outstanding?"],
  "Insurance Payment Report": [
    "How much did BCBS pay last month?",
    "Which payer is slowest to pay us?",
  ],
  IntelliBill: [
    "Why did this claim fail to generate?",
    "What modifiers are valid with 90837?",
    "How do I fix a coding error before approval?",
  ],
  "Claim Approval": [
    "What should I check before approving a claim?",
    "Which claims are waiting on EPIN approval?",
  ],
  Encounter: [
    "Review the coding on this encounter",
    "What modifiers are valid with this CPT?",
    "How do I add an attachment to this claim?",
  ],
  "Patient Insurance": [
    "Is this patient's coverage current, and when was it last verified?",
    "Was this coverage active on the date of service?",
    "Does this patient have active secondary insurance?",
    "How do I run an eligibility check?",
  ],
  "Patient Dashboard": [
    "Summarise this patient's billing history",
    "What does this patient still owe?",
  ],
  "CPT Fee Schedule": [
    "What's our practice fee for CPT 99214?",
    "How do I add a new CPT to this practice?",
  ],
  "Billing Dashboard": [
    "Summarise this month's billing performance",
    "What are our top denial reasons this quarter?",
    "Which provider has the best collection rate?",
  ],
  "Lab Billing": ["Why did this lab claim fail to generate?"],
  Superbill: ["What was billed for this encounter?"],
  "Transaction Logs": ["What changed on this claim and who changed it?"],
  "CPT Report": ["Which CPTs bring in the most revenue?"],
};

/**
 * Fallbacks when the screen has no specific entry. One per capability, in this
 * order, so the first three always demonstrate the full range.
 */
const EVERGREEN = [
  "What should I work first today?", // live data
  "What does denial code CO-16 mean?", // coding / knowledge
  "How do I post an ERA that only partially paid?", // how-to
  "What's our AR over 90 days by payer?", // live data, reporting
];

/**
 * Prompts that use an identifier already on screen. These always rank first, ahead of
 * anything personalised: no other prompt requires zero typing, so none can beat them
 * on payoff regardless of what the biller usually does.
 */
export function getRecordPrompts(pageContext, max = 2) {
  const claimId = pageContext?.ClaimID;
  const patientId = pageContext?.PatientID;

  if (claimId) return claimPrompts(claimId).slice(0, max);
  if (patientId) return PATIENT_PROMPTS.slice(0, max);
  return [];
}

/**
 * The static engine: screen-appropriate prompts topped up with the evergreen set.
 * Used on its own when ChatMS has no personalisation for this biller, and as the
 * top-up tier when it does.
 */
export function getStaticSuggestions(pageContext, max = MAX_SUGGESTIONS) {
  const screen = (pageContext?.sScreenName || "").replace(/\s+V2$/i, "");

  const out = [];
  const add = (prompt) => {
    if (out.length < max && prompt && !out.includes(prompt)) out.push(prompt);
  };

  getRecordPrompts(pageContext).forEach(add);
  (BY_SCREEN[screen] || []).forEach(add);
  EVERGREEN.forEach(add);

  return out;
}
